import React, { useState } from 'react'
import { Dropdown } from 'react-bootstrap'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'
import { SETTINGS_KEYS, environment } from '../../config/environment'
import { useAuth } from '../../hooks/useAuth'
import { useSetting } from '../../hooks/useSettings'
import { authService } from '../../services/auth.service'
import { PermissionAction, PermissionType } from '../../services/permissions.service'
import { useAppStore } from '../../store/useAppStore'
import { useModuleFlag } from '../../utils/featureFlags'
import IfPermissions from '../common/IfPermissions'
import Logo from '../common/Logo'
import { ProfileEditModal } from '../Profile/ProfileEditModal'
import { GlobalSearch } from './GlobalSearch'
import './Navbar.scss'
import { NotificationsDropdown } from './NotificationsDropdown'

export const Navbar: React.FC = () => {
  const { t } = useTranslation()
  const { logout, user } = useAuth()
  const { setIsMenuCollapsed, isMenuCollapsed, uiSettings } = useAppStore()
  const [showProfileModal, setShowProfileModal] = useState(false)
  const displayName = user?.username || t('user')

  const slimSidebar = useSetting<boolean>(SETTINGS_KEYS.SLIM_SIDEBAR, false)
  const appVersion = useSetting<string>(SETTINGS_KEYS.VERSION, '')
  const customAppTitle = useSetting<string>(SETTINGS_KEYS.APP_TITLE, environment.appTitle)
  const customAppSubtitle = useSetting<string | null>(SETTINGS_KEYS.APP_SUBTITLE, environment.appSubtitle)
  const customAppLogoIcon = useSetting<string | null>(SETTINGS_KEYS.APP_LOGO_ICON, null)
  const showAppLogoIcon = useSetting<boolean>(SETTINGS_KEYS.SHOW_APP_LOGO_ICON, true)
  const showAppLogo = useSetting<boolean>(SETTINGS_KEYS.SHOW_APP_LOGO, true)
  const showAppTitle = useSetting<boolean>(SETTINGS_KEYS.SHOW_APP_TITLE, true)
  const showAppSubtitle = useSetting<boolean>(SETTINGS_KEYS.SHOW_APP_SUBTITLE, true)
  const isExternalIntegrationsModuleEnabled = useModuleFlag('external_integrations', true)

  const resolveAssetUrl = (path?: string | null) => {
    if (!path) return null
    if (path.startsWith('http://') || path.startsWith('https://')) return path
    return environment.apiBaseUrl.replace(/\/api\/$/, path)
  }

  const logoIconSrc = resolveAssetUrl(customAppLogoIcon) || environment.appLogoIcon
  const title = customAppTitle?.length ? customAppTitle : environment.appTitle
  const subtitle = (customAppSubtitle && customAppSubtitle.length ? customAppSubtitle : environment.appSubtitle) || t('document_management_system')

  const handleLogout = () => {
    logout()
  }

  const handleEditProfile = () => {
    setShowProfileModal(true)
  }

  const organizations = uiSettings?.organizations || []
  const currentOrganizationId = uiSettings?.currentOrganizationId
  const currentOrganization = organizations.find((organization) => organization.id === currentOrganizationId)

  const handleOrganizationChange = async (organizationId: number) => {
    if (!organizationId || organizationId === currentOrganizationId) {
      return
    }
    await authService.setCurrentOrganization(organizationId)
    window.location.reload()
  }

  return (
    <>
      <header className="app-command-bar shadow-lg">
        <div className="app-command-bar__brand">
          <button
            className="app-command-bar__toggle d-md-none"
            type="button"
            aria-controls="sidebarMenu"
            aria-expanded={!isMenuCollapsed}
            aria-label={t('sidebar')}
            title={t('sidebar')}
            onClick={() => setIsMenuCollapsed(!isMenuCollapsed)}
          >
            <i className="bi bi-list"></i>
          </button>

          <Link
            className={`navbar-brand d-flex align-items-center me-0 order-sm-0 ${slimSidebar ? 'slim' : 'col-auto col-md-3 col-lg-2 col-xxxl-1'}`}
            to="/dashboard"
            draggable={false}
            onDragStart={(e) => e.preventDefault()}
          >
            {(showAppLogoIcon || showAppLogo) && (
              <div className="d-flex align-items-center gap-2">
                {showAppLogoIcon && (
                  <div className="app-command-bar__logo-icon">
                    <img src={logoIconSrc} alt="Logo icon" draggable={false} />
                  </div>
                )}
                {showAppLogo && <Logo height="2em" className="app-command-bar__logo-image" />}
              </div>
            )}

            {(showAppTitle || showAppSubtitle) && (
              <div className="ms-2 ms-md-3 app-title-wrapper">
                {showAppTitle && <span className="app-title">{title}</span>}
                {showAppSubtitle && <span className="app-subtitle">{subtitle}</span>}
              </div>
            )}
          </Link>
        </div>

        <div className="app-command-bar__search">
          <GlobalSearch />
        </div>

        <div className="app-command-bar__actions">
          {organizations.length > 0 && (
            <Dropdown as="div" className="organization-menu">
              <Dropdown.Toggle as="button" className="profile-menu__trigger organization-menu__trigger" id="organizationDropdown">
                <span className="organization-menu__label" title={currentOrganization?.name}>
                  {currentOrganization?.name || t('organization')}
                </span>
              </Dropdown.Toggle>

              <Dropdown.Menu align="end" className="shadow-lg organization-menu__dropdown">
                {organizations.map((organization) => (
                  <Dropdown.Item
                    key={organization.id}
                    active={organization.id === currentOrganizationId}
                    title={organization.name}
                    onClick={() => handleOrganizationChange(organization.id)}
                  >
                    {organization.name}
                  </Dropdown.Item>
                ))}
              </Dropdown.Menu>
            </Dropdown>
          )}

          <NotificationsDropdown />

          <Dropdown as="div" className="profile-menu">
            <Dropdown.Toggle as="button" className="profile-menu__trigger" id="userDropdown">
              <i className="bi bi-person-circle"></i>
              <span className="profile-menu__name d-none d-sm-inline">{displayName}</span>
            </Dropdown.Toggle>

            <Dropdown.Menu align="end" className="shadow-lg profile-menu__dropdown">
              <div className="d-sm-none px-3 py-2 text-muted small">
                {t('logged_in_as')} {displayName}
              </div>
              <Dropdown.Divider />
              <Dropdown.Item onClick={handleEditProfile}>
                <i className="bi bi-person me-2"></i>
                {t('my_profile')}
              </Dropdown.Item>
              <Dropdown.Item as={Link} to="/settings">
                <i className="bi bi-gear me-2"></i>
                {t('settings')}
              </Dropdown.Item>
              {isExternalIntegrationsModuleEnabled && (
                <IfPermissions action={PermissionAction.View} type={PermissionType.User}>
                  <Dropdown.Item as={Link} to="/api-documentation">
                    <i className="bi bi-code-square me-2"></i>
                    {t('api_documentation')}
                  </Dropdown.Item>
                </IfPermissions>
              )}
              <Dropdown.Item onClick={handleLogout}>
                <i className="bi bi-door-open me-2"></i>
                {t('logout')}
              </Dropdown.Item>
              {appVersion && (
                <>
                  <Dropdown.Divider />
                  <div className="px-3 footer-navbar">
                    {t('version')}: {appVersion}
                  </div>
                </>
              )}
            </Dropdown.Menu>
          </Dropdown>
        </div>
      </header>

      <ProfileEditModal isOpen={showProfileModal} onClose={() => setShowProfileModal(false)} />
    </>
  )
}
